import { useCallback, useEffect, useMemo } from 'react';
import { LaunchBulkUpdateAutoFulfill } from 'src/components/common/BulkActions/BulkUpdateAutoFulfillAction';
import { useBulkUpdateAutoFulfillPermissions } from 'src/components/common/BulkActions/BulkUpdateAutoFulfillAction/useBulkUpdateAutoFulfillPermissions';
import { BulkActionItem } from 'src/components/Listings/InventoryActionDropdown/components/types';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { BulkUpdateAutoFulfillDialog } from 'src/dialogs/BulkEdits/BulkUpdateAutoFulfillDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useAnyMarketplaceHandledByProcessor } from 'src/hooks/useMarketplaceHandledByProcessor';
import {
  useUserHasEnableNativeNoFulfillFeature,
  useUserHasFeature,
} from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkEditPreviewWithDetails,
  BulkEditSaleClient,
  CommandOptionType,
  Event,
  Feature,
  SaleQuery,
} from 'src/WebApiController';

import { SALES_BULK_UPDATE_AUTOFULFILL_UPDATE_KEY } from '../../SalesActionDropdown.constants';

export const useBulkUpdateAutoFulfillAction = (
  filterQueryWithEventIds: SaleQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { hasEditAutoFulfillPermissions } = useBulkUpdateAutoFulfillPermissions(
    {
      entityType: ActionOutboxEntityType.Sale,
    }
  );
  const hasAutoFulfillFeature = useUserHasFeature(Feature.AutoFulfillSetting);
  const anyMkpHandledByProcessor = useAnyMarketplaceHandledByProcessor();
  const hasNativeNoFulfillFeature = useUserHasEnableNativeNoFulfillFeature();

  const { activeAccountWebClientConfig } = useAppContext();
  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const updateKey = event
    ? `sales-autofulfill-event-${event.virtId}`
    : SALES_BULK_UPDATE_AUTOFULFILL_UPDATE_KEY;
  const {
    eventsExpansion: { setListItemExpansion, refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkUpdateAutoFulfillDialog,
    closeDialog: closeBulkUpdateAutofulfillDialog,
  } = useDialog(DialogId.BulkUpdateAutoFulfill, BulkUpdateAutoFulfillDialog);

  const onSubmit = useCallback(
    async (
      isNoFulfill: boolean | null,
      _updateSalesOfListings: boolean,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditSaleClient(
              activeAccountWebClientConfig
            ).bulkUpdateIsNoFulfillPreview(filterQueryWithEventIds);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateIsNoFulfillPreview', error, {
              trackErrorData: { filterQueryWithEventIds },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (isNoFulfill != null) {
        await tryInvokeApi(
          async () => {
            const saleClient = new BulkEditSaleClient(
              activeAccountWebClientConfig
            );

            const succeeded = await saleClient.bulkUpdateIsNoFulfill(
              preview!.preview,
              updateKey,
              isNoFulfill,
              supportBackgroundProcess
            );

            if (!supportBackgroundProcess) {
              if (succeeded) {
                // Invalidate the activePosEntity, so that when clicking on listingDetails
                // we force re-loading the listing and newest pricing settings can be displayed
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }

              closeBulkUpdateAutofulfillDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkUpdateIsNoFulfill', error, {
              trackErrorData: { isNoFulfill, preview },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      setIsLoading,
      activeAccountWebClientConfig,
      filterQueryWithEventIds,
      showErrorDialog,
      updateKey,
      closeBulkUpdateAutofulfillDialog,
      setActivePosEntity,
      refreshExpandedListItems,
    ]
  );

  const onChangeClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }
      openBulkUpdateAutoFulfillDialog({
        updateKey,
        onOkay: onSubmit,
        entityType: ActionOutboxEntityType.Sale,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkUpdateAutofulfillDialog,
      });
    },
    [
      closeBulkUpdateAutofulfillDialog,
      event,
      onSubmit,
      openBulkUpdateAutoFulfillDialog,
      setIsLoading,
      setListItemExpansion,
      updateKey,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    if (event) {
      setListItemExpansion(true, [event.virtId]);
    }
    openBulkUpdateAutoFulfillDialog({
      updateKey,
      onOkay: onSubmit,
      entityType: ActionOutboxEntityType.Sale,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkUpdateAutofulfillDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.SalesBulkEditAutoFulfill,
        onSelect: openDialogStableRef,
      },
      disabled:
        (!anyMkpHandledByProcessor && !hasNativeNoFulfillFeature) ||
        !hasEditAutoFulfillPermissions ||
        !hasAutoFulfillFeature,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.SalesBulkEditAutoFulfill,
        disabled:
          (!anyMkpHandledByProcessor && !hasNativeNoFulfillFeature) ||
          !hasEditAutoFulfillPermissions ||
          !hasAutoFulfillFeature,
      });
    };
  }, [
    anyMkpHandledByProcessor,
    hasAutoFulfillFeature,
    hasEditAutoFulfillPermissions,
    hasNativeNoFulfillFeature,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (
      !hasEditAutoFulfillPermissions ||
      !hasAutoFulfillFeature ||
      (!anyMkpHandledByProcessor && !hasNativeNoFulfillFeature)
    ) {
      return [];
    }

    return [
      {
        title: ContentId.UpdateAutoFulfill,
        onClick: onChangeClick,
        disabled: isLoading,
      },
    ];
  }, [
    anyMkpHandledByProcessor,
    hasAutoFulfillFeature,
    hasEditAutoFulfillPermissions,
    hasNativeNoFulfillFeature,
    isLoading,
    onChangeClick,
  ]);

  return {
    dropDown: (
      <LaunchBulkUpdateAutoFulfill
        key="UpdateAutoFulfill"
        onClick={onChangeClick}
        disabled={isLoading}
        entityType={ActionOutboxEntityType.Sale}
      />
    ),
    actionItems,
  };
};
